<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" class="py-0">
        <template v-if="!loading.view">
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-end">
                <div class="full-width">
                  <v-text-field
                    v-model="facilitiesSearchInputValue"
                    class="search-field"
                    :label="
                      $t('message.businessAddresses.list.searchPlaceholder')
                    "
                    prepend-inner-icon="icon-search"
                    clearable
                    outlined
                    rounded
                    :hide-details="true"
                    @input="onFacilitySearch"
                  ></v-text-field>
                </div>
                <ui-button
                  v-if="isFilterApplied"
                  color="primary"
                  outlined
                  @click="removeFilters"
                  class="filter-btn"
                  icon="icon-trash"
                >
                  {{ $t("message.common.removeFilters") }}
                </ui-button>
                <ui-button
                  color="primary"
                  @click="filterDialog = true"
                  class="filter-btn"
                  :class="{ 'filter-btn--filtered': isFilterApplied }"
                  icon="icon-filter"
                >
                  {{ $t("message.common.filter") }}
                </ui-button>
              </div>
            </v-col>
            <v-col cols="12" class="pb-0">
              <div class="d-flex flex-column">
                <Pagination
                  :page="page"
                  :per-page="perPage"
                  :options="perPageOptions"
                  :total-count="totalCount"
                  @change="handlePageChange"
                >
                  <div class="pr-2 d-flex align-center">
                    <ui-button
                      color="primary"
                      @click="onEditSelectedFacilities($event)"
                      :disabled="selectedFacilitiesIds.length === 0"
                      icon="icon-edit"
                      class="mr-2"
                    >
                      {{ $t("message.common.edit") }}
                    </ui-button>
                    <v-tooltip bottom :max-width="500">
                      <template v-slot:activator="{ on }">
                        <div v-on="on">
                          <v-icon color="lime" size="18">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{ $t("message.businessAddresses.list.editTooltip") }}
                    </v-tooltip>
                    <ui-button
                      v-if="
                        isFeatureFlagEnabled(
                          FEATURE_FLAG.SHOW_EDIT_FACILITY_FROM_FILE
                        )
                      "
                      color="primary"
                      @click="onEditFromFileClick"
                      icon="mdi-file-outline"
                      class="ml-4"
                    >
                      {{ $t("message.common.editFromFile") }}
                    </ui-button>
                  </div>
                  <div class="d-flex align-center ml-2">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <ui-button
                          color="primary"
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          :disabled="selectedFacilitiesIds.length === 0"
                          :loading="loading.export"
                          append-icon="icon-arrow-down"
                          >{{ $t("message.common.exportTo2") }}
                        </ui-button>
                      </template>
                      <v-list class="export-type-list">
                        <v-list-item
                          v-for="exportFacilityType in exportFacilitiesTypes"
                          :key="exportFacilityType.id"
                          :value="exportFacilityType.type"
                          class="export-type-list__item"
                          @click="handleExportFacilities(exportFacilityType)"
                        >
                          {{ exportFacilityType.text }}</v-list-item
                        ></v-list
                      >
                    </v-menu>
                  </div>
                </Pagination>
                <div
                  class="facilities__select-wrapper"
                  v-if="currentPageFacilities.length > 0"
                >
                  <div class="d-flex align-center">
                    <v-checkbox
                      class="facilities__select-all"
                      :input-value="isAllFacilitiesSelected"
                      :label="$t('message.common.selectAll')"
                      @change="toggleAllFacilities"
                    ></v-checkbox>

                    <ItemsCounter
                      class="ml-10"
                      :count="totalCount"
                      prefixText="message.businessAddresses.list.totalFacilitiesCount"
                    />
                  </div>
                  <ItemsCounter
                    v-if="selectedFacilitiesCount"
                    :count="selectedFacilitiesCount"
                  />
                </div>
              </div>
            </v-col>
            <v-col>
              <v-virtual-scroll
                v-if="currentPageFacilities.length > 0"
                :items="currentPageFacilities"
                :item-height="listItemHeight"
                :height="virtualScrollHeight"
                class="facilities__list"
                ref="listContainer"
              >
                <template v-slot:default="{ item }">
                  <v-list-item
                    :key="item.id"
                    :ripple="false"
                    :class="{
                      active:
                        !!selectedBusinessAddress &&
                        item.id === selectedBusinessAddress.id
                    }"
                    @click="setSelectedBusinessAddress(item)"
                    class="list-item"
                  >
                    <v-checkbox
                      :disabled="
                        !item.facilityListings.isOwner ||
                          item.facilityListings.addressGroup.gmbAddressId ===
                            null
                      "
                      :input-value="isFacilitySelected(item.id)"
                      @change="toggleFacility(item)"
                    ></v-checkbox>
                    <IsOwnerIndicator
                      :isOwner="item.facilityListings.isOwner"
                      height="24px"
                      class="mx-3"
                    />
                    <v-list-item-content
                      class="d-flex justify-space-between flex-nowrap"
                    >
                      <div class="list-item__content-primary">
                        <p class="list-item__name">{{ item.name }}</p>
                        <FacilityAddress
                          :formatted-address="item.formattedAddress"
                          :store-code="item.storeCode"
                        />
                      </div>
                      <div
                        v-if="
                          isFeatureFlagEnabled(FEATURE_FLAG.SHOW_INTEGRATIONS)
                        "
                        class="list-item__content-statuses"
                      >
                        <IntegrationStatuses
                          :integrations="item.mapsIntegrationStatus"
                        />
                      </div>
                      <div class="list-item__content-secondary">
                        <div class="list-item__statuses">
                          <template
                            v-if="
                              item.facilityListings.addressGroup
                                .gmbAddressId !== null
                            "
                          >
                            <FacilityStatusItem
                              v-if="item.openStatus"
                              :label="
                                $t(`message.openStatusTypes.${item.openStatus}`)
                              "
                              :type="item.openStatus"
                              class="mb-1"
                            />
                            <FacilityStatusItem
                              v-if="
                                item.locationState &&
                                  !isFeatureFlagEnabled(
                                    FEATURE_FLAG.SHOW_INTEGRATIONS
                                  )
                              "
                              :label="item.locationState.state"
                              :code="item.locationState.code"
                            />
                          </template>
                          <template v-else>
                            <div class="d-flex align-center">
                              <v-icon color="accent" size="17.5" class="mr-2">
                                mdi-information
                              </v-icon>
                              <p
                                class="list-item__status--adding-to-google-maps"
                              >
                                {{
                                  $t(
                                    "message.businessAddresses.list.addingToGoogleMaps"
                                  )
                                }}
                              </p>
                            </div>
                          </template>
                        </div>
                        <ui-button
                          transparent
                          small
                          class="list-item__show-facility-btn"
                          @click="handleShowFacility(item.id)"
                        >
                          {{ $t("message.common.showFacility") }}
                        </ui-button>
                      </div>
                    </v-list-item-content>
                    <v-menu
                      left
                      :offset-x="true"
                      origin="left top"
                      nudge-top="5"
                      nudge-left="5"
                    >
                      <template #activator="{ on }">
                        <v-btn
                          icon
                          v-on="on"
                          :disabled="
                            item.facilityListings.addressGroup.gmbAddressId ===
                              null
                          "
                        >
                          <v-icon color="primary">icon-more</v-icon>
                        </v-btn>
                      </template>
                      <ui-button
                        outline
                        @click="openDelete(item)"
                        icon="icon-trash"
                        icon-color="pink"
                      >
                        {{ $t("message.businessAddresses.list.delete") }}
                      </ui-button>
                    </v-menu>
                  </v-list-item>
                </template>
              </v-virtual-scroll>
              <no-data v-else></no-data>
              <SectionLoader :status="loading.list" />
            </v-col>
          </v-row>
        </template>
        <div v-else class="loading-wrapper">
          <SectionLoader :status="loading.view" />
        </div>
      </v-col>
      <ConfirmationDialog
        :isOpen="confirmationDialog"
        :body="confirmationDialogBody"
        @closeDialog="confirmationDialog = false"
        @delete="deleteFromList(itemToDelete)"
      />

      <FilterDialog
        v-if="filterDialog"
        :show-facilities="false"
        show-open-status
        show-location-status
        :show-bing-location-status="showBingFilter"
        :cities="cities"
        :facilities-groups="facilitiesGroupsIds"
        :chosen-search-type="searchType"
        :chosen-open-status="status.open"
        :chosen-location-status="status.location"
        :chosen-bing-location-status="status.bing"
        @setCities="cities = $event"
        @setFacilitiesGroupsIds="facilitiesGroupsIds = $event"
        @setSearchType="searchType = $event"
        @setOpenStatus="status.open = $event"
        @setLocationStatus="status.location = $event"
        @setBingLocationStatus="status.bing = $event"
        @change="onFiltersChange({ ...$event })"
        :handleClose="closeFilterDialog"
      />
    </v-row>
    <AsyncDownloadDialog
      v-if="showAsyncDownloadDialog"
      :export-id="exportId"
      @close="showAsyncDownloadDialog = false"
    />
    <EditFromFileDialog
      v-if="showEditFromFileDialog"
      @close="showEditFromFileDialog = false"
    />
  </v-container>
</template>

<script>
import facilitiesService from "@/services/facilities";
import serviceHandler from "@/helpers/handlers/service";
import { numbered } from "@/config/marker.js";
import { mapActions, mapGetters, mapState } from "vuex";
import { debounce } from "@/helpers/helpers";
import { gtmEvent__buttonClick } from "@/helpers/gtm-events";
import cuid from "cuid";
import { FILE_TYPE_ENUM } from "@/enums/file-type";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { FACILITIES_FILTER_SEARCH_TYPES } from "@/enums/facilty-details-status.enum";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { queryParamsMixin } from "@/helpers/mixins/query-params";

import SectionLoader from "@/components/Loader/SectionLoader.vue";
import ItemsCounter from "@/components/List/ItemsCounter";
import NoData from "@/components/ui/NoData";
import ConfirmationDialog from "@/components/ListingsConfiguration/Dialog/Confirmation.vue";
import Pagination from "@/components/List/Pagination";
import FilterDialog from "@/components/Filter/dialog";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";
import FacilityStatusItem from "@/components/BusinessAddresses/FacilityStatusItem.vue";
import IntegrationStatuses from "@/components/BusinessAddresses/IntegrationStatuses.vue";

import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "BusinessAddressesList",
  components: {
    SectionLoader,
    ItemsCounter,
    NoData,
    ConfirmationDialog,
    Pagination,
    FilterDialog,
    IsOwnerIndicator,
    FacilityStatusItem,
    IntegrationStatuses,
    FacilityAddress: () =>
      import("@/components/BusinessAddresses/FacilityAddress"),
    AsyncDownloadDialog: () => import("@/components/AsyncDownloadDialog"),
    EditFromFileDialog: () =>
      import("@/components/BusinessAddresses/EditFromFileDialog")
  },
  mixins: [gtmEngagementMixin, queryParamsMixin],

  data() {
    return {
      FEATURE_FLAG,
      markerType: { numbered },
      selectedBusinessAddress: null,
      loading: {
        view: false,
        list: false,
        export: false
      },
      page: 1,
      perPage: 200,
      perPageOptions: [50, 100, 200, 500],
      listItemHeight: 66,
      confirmationDialog: false,
      confirmationDialogBody: {
        isError: false,
        title: "",
        text: "",
        hasCancelButton: false,
        hasDeleteButton: false
      },
      itemToDelete: {},
      exportFacilitiesTypes: [
        {
          id: cuid(),
          text: this.$t("message.common.csv"),
          value: FILE_TYPE_ENUM.CSV
        },
        {
          id: cuid(),
          text: this.$t("message.common.xlsx"),
          value: FILE_TYPE_ENUM.XLSX
        }
        // hide excel exports CN-1216
        // {
        //   id: cuid(),
        //   text: this.$t("message.common.xls"),
        //   value: FILE_TYPE_ENUM.XLS
        // },
      ],
      facilitiesSearchInputValue: "",
      filterDialog: false,
      cities: [],
      gmbAddressesIds: [],
      facilitiesGroupsIds: [],
      status: {
        open: 0,
        location: 0,
        bing: 0
      },
      searchType: null,
      showAsyncDownloadDialog: false,
      isExportOpened: false,
      exportId: null,
      fileTypes: {
        [FILE_TYPE_ENUM.CSV]: "csv",
        [FILE_TYPE_ENUM.XLSX]: "xlsx"
      },
      showEditFromFileDialog: false
    };
  },

  computed: {
    ...mapGetters({
      isAllFacilitiesSelected: "facilities/isAllFacilitiesSelected",
      selectedFacilitiesIds: "facilities/selectedFacilitiesIds",
      selectedFacilitiesCount: "facilities/selectedFacilitiesCount",
      isFacilitySelected: "facilities/isFacilitySelected"
    }),

    ...mapState({
      currentPageFacilities: state => state.facilities.currentPageFacilities,
      totalCount: state => state.facilities.totalCount
    }),
    virtualScrollHeight() {
      return this.currentPageFacilities.length < 8
        ? this.listItemHeight * (this.currentPageFacilities.length + 1)
        : this.listItemHeight * 8;
    },
    isFilterApplied() {
      return (
        this.cities.length ||
        this.facilitiesGroupsIds.length ||
        this.status.open !== 0 ||
        this.status.location !== 0 ||
        this.status.bing !== 0
      );
    },
    showBingFilter() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_5906_FILTER_BING_STATUS);
    }
  },

  async created() {
    this.loading.view = true;
    this.setInitialFilters();
    await this.initFacilities({
      page: this.page,
      perPage: this.perPage,
      isOwner: false,
      allFacilitiesIsOwner: true,
      withoutGmbAddress: true,
      query: this.facilitiesSearchInputValue,
      cities: this.cities,
      facilitiesIds: this.gmbAddressesIds,
      groups: this.facilitiesGroupsIds,
      locationState: this.status.location,
      openStatus: this.status.open,
      bingLocationStatus: this.status.bing,
      fetchAllFacilitiesIds: true
    });
    this.setSelectedBusinessAddress(this.currentPageFacilities[0]);
    this.loading.view = false;
  },

  mounted() {
    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  methods: {
    isFeatureFlagEnabled,
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore",
      initFacilities: "facilities/initFacilities",
      selectFacilities: "facilities/selectFacilities",
      toggleFacility: "facilities/toggleFacility",
      toggleAllFacilities: "facilities/toggleAllFacilities",
      setSelectedGroupsIds: "facilitiesGroups/setSelectedGroupsIds"
    }),
    onEditFromFileClick() {
      this.showEditFromFileDialog = !this.showEditFromFileDialog;
    },
    setInitialFilters() {
      const {
        cities,
        facilitiesGroupsIds,
        open,
        location,
        search,
        page,
        perPage
      } = this.$route.query;
      if (cities) {
        this.cities = cities.split(",");
        this.searchType = FACILITIES_FILTER_SEARCH_TYPES.CITY;
      }
      if (search) {
        this.facilitiesSearchInputValue = search;
      }
      if (facilitiesGroupsIds) {
        this.facilitiesGroupsIds = facilitiesGroupsIds
          .split(",")
          .map(id => parseInt(id));
        this.searchType = FACILITIES_FILTER_SEARCH_TYPES.FACILITIES_GROUPS;
        this.setSelectedGroupsIds(this.facilitiesGroupsIds);
      }
      if (open) {
        this.status.open = parseInt(open);
      }
      if (location) {
        this.status.location = location;
      }
      if (page) {
        this.page = parseInt(page);
      }
      if (perPage) {
        this.perPage = parseInt(perPage);
      }
    },
    async removeFilters() {
      this.loading.list = true;
      this.cities = [];
      this.facilitiesGroupsIds = [];
      this.status = {
        open: 0,
        location: 0,
        bing: 0
      };
      this.updateQueryParams({
        cities: null,
        facilitiesGroupsIds: null,
        open: 0,
        location: 0,
        bing: 0
      });

      await this.fetchFacilities();
      this.loading.list = false;
    },

    setSelectedBusinessAddress(businessAddress) {
      this.selectedBusinessAddress = businessAddress;
    },
    async exportFile(type) {
      try {
        this.loading.export = true;
        const { export_id } = await serviceHandler(
          facilitiesService.exportFacilities({
            type: this.fileTypes[type],
            ids: this.selectedFacilitiesIds
          })
        );
        this.loading.export = false;

        this.exportId = export_id;
        this.showAsyncDownloadDialog = true;
      } catch (err) {
        this.loading.export = false;
      }
    },
    openDelete(item, id = null) {
      this.itemToDelete = item;
      this.confirmationDialogBody = {
        isError: false,
        title: this.$t(
          "message.companyProfile.listings.confirmationDialog.deleteFacilityConfirm"
        ),
        text: "",
        hasCancelButton: true,
        hasDeleteButton: true,
        type: "account",
        type_id: id
      };

      this.confirmationDialog = true;
    },

    async deleteFromList(businessAddress) {
      this.confirmationDialog = false;
      this.loading.list = true;
      this.selectedBusinessAddress = null;
      await serviceHandler(
        facilitiesService.deleteFacility(businessAddress.id)
      );
      this.itemToDelete = {};

      // setting last available page if exceeded
      if (this.totalCount - 1 <= (this.page - 1) * this.perPage) {
        this.page--;
      }

      await this.fetchFacilities();
      this.setSelectedBusinessAddress(this.currentPageFacilities[0]);
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATIONS_DISCONNECT,
        location_id: businessAddress.id
      });
      this.loading.list = false;
    },

    async handlePageChange({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      this.updateQueryParams({ page, perPage });
      this.loading.list = true;
      await this.fetchFacilities();
      this.$refs.listContainer.$el.scrollTop = 0;
      this.loading.list = false;
    },

    onEditSelectedFacilities() {
      const selectedFacilitiesIds = [...this.selectedFacilitiesIds];
      if (selectedFacilitiesIds.length === 1) {
        this.$router.push({
          name: "profile-facility-details",
          params: {
            id: selectedFacilitiesIds[0]
          },
          query: { back: this.$route.fullPath }
        });
      } else {
        this.$router.push({
          name: "profile-facility-edit-selected",
          params: {
            ids: selectedFacilitiesIds.join(",")
          },
          query: { back: this.$route.fullPath }
        });
      }
    },

    handleExportFacilities(exportFacilityType) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATIONS_EXPORT,
        format: exportFacilityType
          ? exportFacilityType.text.toLowerCase()
          : null,
        num_locations: this.selectedFacilitiesIds
          ? this.selectedFacilitiesIds.length
          : null
      });

      this.exportFile(exportFacilityType.value);
    },

    onFacilitySearch: debounce(async function() {
      this.loading.list = true;
      this.page = 1;
      await this.fetchFacilities();
      this.loading.list = false;
      const currentSearch = this.$route.query.search;
      if (currentSearch !== this.facilitiesSearchInputValue) {
        this.updateQueryParams({ search: this.facilitiesSearchInputValue });
      }
    }, 500),

    async fetchFacilities() {
      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.facilitiesSearchInputValue,
        isOwner: false,
        withoutGmbAddress: true,
        cities: this.cities,
        facilitiesIds: this.gmbAddressesIds,
        groups: this.facilitiesGroupsIds,
        locationState: this.status.location,
        openStatus: this.status.open,
        bingLocationStatus: this.status.bing,
        allFacilitiesIsOwner: true,
        fetchAllFacilitiesIds: true
      };

      await this.$store.dispatch("facilities/fetchFacilities", params);
      this.setSelectedBusinessAddress(this.currentPageFacilities[0]);
    },

    async onFiltersChange({
      gmbAddressesIds = this.gmbAddressesIds,
      cities = this.cities,
      facilitiesGroupsIds = this.facilitiesGroupsIds,
      openStatus = this.status.open,
      locationStatus = this.status.location,
      bingLocationStatus = this.status.bing
    }) {
      this.loading.list = true;
      this.page = 1;

      this.gmbAddressesIds = gmbAddressesIds;
      this.cities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;
      this.status.open = openStatus;
      this.status.location = locationStatus;
      this.status.bing = bingLocationStatus;

      this.updateQueryParams({
        cities: cities.join(","),
        facilitiesGroupsIds: facilitiesGroupsIds.join(","),
        open: openStatus,
        location: locationStatus,
        bing: bingLocationStatus
      });

      await this.fetchFacilities();

      this.loading.list = false;
    },

    closeFilterDialog() {
      this.selectFacilities([]);
      this.filterDialog = false;
    },

    async onCitiesFilter(cities) {
      this.loading.list = true;
      this.cities = cities;
      this.page = 1;
      await this.fetchFacilities();
      this.loading.list = false;
    },

    handleShowFacility(id) {
      gtmEvent__buttonClick({ label: "show_location" });

      this.$router.push({
        name: "facility-details",
        params: { id },
        query: { back: this.$route.fullPath }
      });
    },

    getFacilityDescription(facility) {
      const storeLabel = facility.storeCode
        ? ` | ${this.$t("message.common.storeCode")}: ${facility.storeCode}`
        : "";
      return `${facility.formattedAddress}${storeLabel}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.export-type-list {
  &__item {
    @include font-size(13px);
    min-height: 40px;
  }
}
.loading-wrapper {
  min-height: 600px;
}

.search-field {
  max-width: 645px;
}

.export-label {
  @include font-size(14px);
  line-height: 24px;
  opacity: 0.7;
  margin: 0 $spacing;
}

.facilities {
  &__select-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: $spacing;
  }

  &__list {
    border-radius: 4px;
    margin-bottom: $spacing * 3;
    border: 1px solid $gray-lighter;
  }
}

.list-actions__select {
  max-width: 110px;
}

.v-list-item__content > * {
  flex: unset;
}

.list-item {
  &:nth-child(odd):not(.active) {
    background-color: #fcfcfc;
  }

  &__content-primary {
    max-width: calc(100% - 415px);
    width: 30%;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__content-statuses {
    max-width: calc(100% - 415px);
    min-width: 200px;
    width: 40%;
    text-overflow: ellipsis;
    overflow: hidden;
    padding: 0 $spacing * 2;
    display: flex;
    justify-content: center;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }

  &__address {
    margin-right: $spacing;
    position: relative;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__store-code {
    margin-left: $spacing * 1.25;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__content-secondary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 30%;
    min-width: 300px;
    margin-right: $spacing * 4;
  }

  &__statuses {
    display: flex;
    flex-direction: column;
  }

  &__status--adding-to-google-maps {
    @include font-size(14px);
    line-height: 17px;
    opacity: 0.6;
    margin-bottom: 0;
  }

  &__show-facility-btn {
    @include font-size(14px);
    text-decoration: underline;
    font-weight: 600;
  }
}

.active {
  background-color: rgba($primary, 0.25);
}

.btn-action-menu.v-menu__content {
  box-shadow: none;
  border-radius: $container-border-radius;
  filter: $shadow-filter;
  margin-left: $spacing * -6;
}

.item-menu-desc::after {
  display: none;
}

.right-separator {
  padding-right: $spacing * 5;
  position: relative;
}

.right-separator::after {
  content: "";
  position: absolute;
  right: 20px;
  top: 50%;
  height: 28px;
  width: 1px;
  background-color: $base-light-2;
  transform: translateY(-50%);
}

.right-separator.v-input--checkbox::after {
  right: 25px;
  top: 32%;
}

.bottom-line {
  position: relative;
}
.bottom-line::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-color: $gray-200;
  bottom: -14px;
}

.border-right {
  border-right: 1px solid $gray-1;
  padding-right: $spacing;
  padding-top: 1px;
}

.filter-btn {
  position: relative;
  margin-left: $spacing;

  &--filtered {
    &::after {
      content: "";
      position: absolute;
      top: 12px;
      left: 25px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: $lime;
    }
  }
}

.add-facility-btn {
  color: $light;
}
</style>

<style lang="scss">
.facilities__select-all.v-input--selection-controls .v-input__slot > .v-label {
  @include font-size(14px);
}
</style>
